import { ENVIRONMENT_INITIALIZER, EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslationService } from '@core/services/translation.service';

type FieldErrorTranslator = (key: string, fallback: string, params?: Record<string, string | number>) => string;

/** English-only stand-in until the app registers TranslationService (and in unit tests). */
const englishFieldErrors: FieldErrorTranslator = (_key, fallback, params) =>
  params ? fallback.replace(/\{(\w+)\}/g, (m, name: string) => (name in params ? String(params[name]) : m)) : fallback;

let translateFieldError: FieldErrorTranslator = englishFieldErrors;

/**
 * getFieldError() is a plain function called straight from ~40 templates, so it
 * cannot inject. The app registers TranslationService here once at startup
 * (see provideFieldErrorTranslations in app.config); t() reads the language
 * signal, so the messages follow a language switch on the next render.
 */
export function setFieldErrorTranslator(translator: FieldErrorTranslator | null): void {
  translateFieldError = translator ?? englishFieldErrors;
}

export function provideFieldErrorTranslations(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const i18n = inject(TranslationService);
        setFieldErrorTranslator((key, fallback, params) => i18n.t(key, fallback, params));
      },
    },
  ]);
}

export class FormValidator {

  // ✅ Fast-fail validation - return early on invalid conditions
  static required(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value.toString().trim().length === 0) {
      return { required: { message: 'This field is required' } };
    }
    return null;
  }

  static email(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(control.value);

    return isValid ? null : {
      email: {
        message: 'Please enter a valid email address',
        actualValue: control.value
      }
    };
  }

  static minLength(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const actualLength = control.value.toString().length;
      const isValid = actualLength >= minLength;

      return isValid ? null : {
        minLength: {
          message: `Minimum length is ${minLength} characters`,
          requiredLength: minLength,
          actualLength
        }
      };
    };
  }

  static maxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const actualLength = control.value.toString().length;
      const isValid = actualLength <= maxLength;

      return isValid ? null : {
        maxLength: {
          message: `Maximum length is ${maxLength} characters`,
          requiredLength: maxLength,
          actualLength
        }
      };
    };
  }
}

// Business-specific validators extending FormValidator
export class BusinessFormValidator extends FormValidator {

  // Saudi mobile number validation (9 digits starting with 5, or 12 digits starting with 9665)
  static saudiMobileValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString().replace(/\s/g, '');
    const isValid = /^(966)?5\d{8}$/.test(value);

    return isValid ? null : {
      saudiMobile: {
        message: 'Mobile must be 9 digits starting with 5',
        actualValue: control.value
      }
    };
  }

  // Saudi IBAN validation (22 characters starting with SA)
  static saudiIbanValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString().replace(/\s/g, '');
    const isValid = /^SA\d{20}$/.test(value);

    return isValid ? null : {
      saudiIban: {
        message: 'IBAN must be 22 characters starting with SA',
        actualValue: control.value
      }
    };
  }

  // Arabic keyboard validation
  static arabicKeyboardValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s\d\-_.,!@#$%^&*()]+$/;
    const isValid = arabicRegex.test(control.value);

    return isValid ? null : {
      arabicKeyboard: {
        message: 'Please use Arabic keyboard only',
        actualValue: control.value
      }
    };
  }

  // English keyboard validation
  static englishKeyboardValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const englishRegex = /^[A-Za-z\s\d\-_.,!@#$%^&*()]+$/;
    const isValid = englishRegex.test(control.value);

    return isValid ? null : {
      englishKeyboard: {
        message: 'Please use English keyboard only',
        actualValue: control.value
      }
    };
  }

  // Future date validation
  static futureDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const inputDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return inputDate > today ? null : {
      futureDate: {
        message: 'Date must be in the future',
        actualValue: control.value
      }
    };
  }

  // Exact digits validation factory
  static exactDigitsValidator(digits: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const value = control.value.toString().replace(/\s/g, '');
      const regex = new RegExp(`^\\d{${digits}}$`);
      const isValid = regex.test(value);

      return isValid ? null : {
        exactDigits: {
          message: `Must be exactly ${digits} digits`,
          requiredLength: digits,
          actualLength: value.length
        }
      };
    };
  }

  // File type validation factory
  static fileTypeValidator(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value;
      if (!file) return null;

      const fileType = file.type;
      const isValid = allowedTypes.includes(fileType);

      return isValid ? null : {
        fileType: {
          message: `File must be one of: ${allowedTypes.join(', ')}`,
          allowedTypes,
          actualType: fileType
        }
      };
    };
  }

  // File size validation factory
  static fileSizeValidator(maxSizeBytes: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value;
      if (!file) return null;

      const isValid = file.size <= maxSizeBytes;

      return isValid ? null : {
        fileSize: {
          message: `File size must not exceed ${maxSizeBytes / 1024 / 1024}MB`,
          maxSize: maxSizeBytes,
          actualSize: file.size
        }
      };
    };
  }

  // Saudi phone number validation (max 9 digits)
  static saudiPhoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString().replace(/\s/g, '');
    const isValid = /^\d{1,9}$/.test(value);

    return isValid ? null : {
      saudiPhone: {
        message: 'Phone number must be maximum 9 digits',
        actualValue: control.value
      }
    };
  }

  // VAT percentage validation (0-100)
  static vatPercentageValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = parseFloat(control.value);
    const isValid = !isNaN(value) && value >= 0 && value <= 100;

    return isValid ? null : {
      vatPercentage: {
        message: 'VAT percentage must be between 0 and 100',
        actualValue: control.value
      }
    };
  }

  // Building number validation (4 digits)
  static buildingNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString();
    const isValid = /^\d{4}$/.test(value);

    return isValid ? null : {
      buildingNumber: {
        message: 'Building number must be exactly 4 digits',
        actualValue: control.value
      }
    };
  }

  // Additional number validation (4 digits)
  static additionalNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString();
    const isValid = /^\d{4}$/.test(value);

    return isValid ? null : {
      additionalNumber: {
        message: 'Additional number must be exactly 4 digits',
        actualValue: control.value
      }
    };
  }

  // Zip code validation (5 digits)
  static zipCodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString();
    const isValid = /^\d{5}$/.test(value);

    return isValid ? null : {
      zipCode: {
        message: 'Zip code must be exactly 5 digits',
        actualValue: control.value
      }
    };
  }

  // Short address validation (8 characters)
  static shortAddressValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const value = control.value.toString();
    const isValid = value.length === 8;

    return isValid ? null : {
      shortAddress: {
        message: 'Short address must be exactly 8 characters',
        actualValue: control.value
      }
    };
  }

}

export function isFieldInvalid(
  fieldName: string,
  formGroup: FormGroup,
  formGroupName?: string,
  index?: number
): boolean {
  const controlPath = buildControlPath(fieldName, formGroupName, index);
  const field = formGroup.get(controlPath);
  return !!(field && field.invalid && (field.dirty || field.touched));
}

export function getFieldError(
  fieldName: string,
  formGroup: FormGroup,
  formGroupName?: string,
  index?: number
): string {
  const controlPath = buildControlPath(fieldName, formGroupName, index);
  const field = formGroup.get(controlPath);

  if (!field || !field.errors || !(field.dirty || field.touched)) return '';

  const isInvalidStart = formGroup.errors?.['invalidStart'];
  const isLengthInvalid = !!field.errors?.['minlength'] || !!field.errors?.['maxlength'];

  const t = translateFieldError;
  const mobileHint = () => t('form.mobileNumberHelper', '9 digits starting with 5');
  const invalidType = () => t('validation.invalidType', 'Invalid type');

  if (isInvalidStart && isLengthInvalid) return mobileHint();
  if (field.errors['required']) return t('validation.required', 'This field is required');
  if (field.errors['email']) return t('validation.email', 'Please enter a valid email address');
  if (field.errors['pattern']) {
    if (fieldName === 'mobileNumber') return mobileHint();
    if (fieldName === 'buildingNumber' || fieldName === 'additionalNumber') return t('form.exactly4Digits', 'Must be exactly 4 digits');
    if (fieldName === 'zipCode') return t('form.exactly5Digits', 'Must be exactly 5 digits');
    if (fieldName === 'shortAddress') return t('form.exactly8Chars', 'Must be exactly 8 characters');
    return invalidType();
  }
  if (field.errors['arabicKeyboard'] || field.errors['englishKeyboard']) return invalidType();
  if (field.errors['arabicOnly'] || field.errors['englishOnly']) return invalidType();
  if (field.errors['minlength']) {
    return t('validation.minCharsRequired', 'Minimum {n} characters required', { n: field.errors['minlength'].requiredLength });
  }
  if (field.errors['maxlength']) {
    return t('validation.maxCharsAllowed', 'Maximum {n} characters allowed', { n: field.errors['maxlength'].requiredLength });
  }
  if (field.errors['max']) return t('validation.rangeFromZero', 'Must be from 0 to {n}', { n: field.errors['max'].max });
  if (field.errors['containsArabic']) {
    return t(
      'validation.noArabic',
      'Arabic characters are not allowed. Please use English characters, numbers, and standard punctuation only.',
    );
  }
  if (field.errors['sameAsIndividualMobile']) {
    return t('validation.sameAsIndividualMobile', "Cannot be the individual's own mobile number");
  }
  // Set by the check-mobile lookup. Reported ahead of the generic messages because
  // it is the one the admin cannot fix by correcting the format.
  if (field.errors['mobileRegistered']) return t('editRequest.dupMobile', 'This mobile number already belongs to a WAKI account.');
  if (field.errors['duplicateDepMobile']) {
    return t('editRequest.dupDepMobile', 'Another dependent in this request already uses this mobile number.');
  }
  if (field.errors['expirationBeforeIssuing']) {
    return t('validation.expirationAfterIssuing', 'Expiration date must be after issuing date');
  }

  return t('validation.invalidInput', 'Invalid input');
}

function buildControlPath(
  fieldName: string,
  formGroupName?: string,
  index?: number
): string {
  if (formGroupName && index !== undefined) {
    // For FormArray inside FormGroup: e.g. 'studentsInformation.dependents.0.fullNameAr'
    return `${formGroupName}.${index}.${fieldName}`;
  } else if (formGroupName) {
    // For FormGroup: e.g. 'adultInformation.fullNameAr'
    return `${formGroupName}.${fieldName}`;
  } else {
    // Top-level control
    return fieldName;
  }
}

export function startCorrect(path: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const control = group.get(path);
    if (!control || !control.value) return null;

    const value = control.value.toString();
    // Allow starting with 5 or 9665
    const isValid = /^5/.test(value) || /^9665/.test(value);

    if (!isValid) {
      return { invalidStart: true };
    }

    return null;
  };
}